import React from "react";
import { motion } from "framer-motion";
import { Token } from "../../types/tournament";
import { VictoryPhase } from "./types";
import { tokenAvatarVariants } from "./animations";
import { useTheme } from "../../contexts/ThemeContext";

interface TokenAvatarProps {
  token: Token;
  side: "left" | "right";
  sizeClass: string;
  victoryPhase: VictoryPhase;
  isEliminated: boolean;
  isWinner: boolean;
  isDead: boolean;
  innerRef?: React.RefObject<HTMLDivElement>;
}

const TokenAvatar: React.FC<TokenAvatarProps> = ({
  token,
  side,
  sizeClass,
  victoryPhase,
  isEliminated,
  isWinner,
  isDead,
  innerRef,
}) => {
  const { colors } = useTheme();
  
  const getAvatarVariant = () => {
    if (isEliminated) {
      if (victoryPhase === "elimination") {
        return side === "left" ? "eliminationLeft" : "eliminationRight";
      }
      if (victoryPhase === "transfer") return "draining";
      if (victoryPhase === "complete") return "dead";
    }
    if (isWinner && victoryPhase === "complete") {
      return side === "left" ? "winnerLeft" : "winnerRight";
    }
    return "idle";
  };

  const getGradientColors = () => {
    if (isDead) return "from-gray-600 to-gray-800";
    return side === "left" 
      ? colors.teamA.gradient
      : colors.teamB.gradient;
  };

  const positionClass = side === "left" ? "left-2 sm:left-3 md:left-4" : "right-2 sm:right-3 md:right-4";

  return (
    <div
      ref={innerRef}
      className={`absolute ${positionClass} top-1 sm:top-2 ${sizeClass}`}
    >
      <motion.div
        className={`w-full h-full rounded-full ${
          token.imagePath ? "" : `bg-gradient-to-br ${getGradientColors()}`
        } relative overflow-hidden`}
        variants={tokenAvatarVariants}
        initial="idle"
        animate={getAvatarVariant()}
      >
        {token.imagePath && (
          <img
            src={`/tokens/images/${token.imagePath}`}
            alt={token.name}
            className={`w-full h-full object-cover ${
              isDead ? "opacity-50 grayscale" : ""
            }`}
          />
        )}
      </motion.div>
    </div>
  );
};

export default TokenAvatar;